import React, { useEffect, useState } from 'react';
import { CheckCircle, Hourglass, MailX, Send, UserMinus } from 'lucide-react';
import StatCard from '../../../../components/common/StatCard';
import { SkeletonStatCards } from '../../../../components/common/SkeletonLoader';
import { EDM_UI, edmErrorMessage } from '../../../../utils/edm';
import type { EdmStats } from '../../../../hooks/useEdmCampaigns';

const POLL_MS = 60_000;
const PENDING_SUBTITLE = 'Delivery tracking arrives soon';

/** A count the server may not know yet; unknown shows "–", never 0. */
function measured(v: number | null | undefined): { value: string; subtitle?: string } {
  return typeof v === 'number' ? { value: v.toLocaleString('en-AU') } : { value: '–', subtitle: PENDING_SUBTITLE };
}

const BREAKDOWN: Array<{ key: keyof EdmStats; label: string }> = [
  { key: 'blockedSuppressed', label: 'Skipped: suppressed' },
  { key: 'blockedConsentWithdrawn', label: 'Skipped: consent withdrawn' },
  { key: 'blockedOutOfScope', label: 'Skipped: no longer in your offices' },
  { key: 'blockedSourceMissing', label: 'Skipped: source removed' },
  { key: 'failedInvalidRecipient', label: 'Failed: invalid address' },
  { key: 'failedProvider', label: 'Failed at provider' },
  { key: 'excludedInvalidEmail', label: 'Excluded at freeze: invalid email' },
  { key: 'unknown', label: 'Unknown (awaiting confirmation)' },
  { key: 'complained', label: 'Complaints' },
];

/**
 * Per-campaign counters from getStats. Polled every 60 s while sending,
 * loaded once otherwise. No chart and no export.
 */
const StatsPanel: React.FC<{
  campaignId: string;
  status: string;
  getStats: (campaignId: string) => Promise<EdmStats>;
}> = ({ campaignId, status, getStats }) => {
  const [stats, setStats] = useState<EdmStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      getStats(campaignId)
        .then((s) => {
          if (!cancelled) {
            setStats(s);
            setError(null);
          }
        })
        .catch((err) => {
          if (!cancelled) setError(edmErrorMessage(err));
        });
    void load();
    const timer = status === 'sending' ? setInterval(load, POLL_MS) : null;
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [campaignId, status, getStats]);

  if (error) return <div className={EDM_UI.errorBanner}>{error}</div>;
  if (!stats) return <SkeletonStatCards count={5} />;
  if (!(stats.sent > 0)) return null;

  const delivered = measured(stats.delivered);
  const bounced = measured(stats.bounced);
  const unsubscribed = measured(stats.unsubscribed);
  const rows = BREAKDOWN.filter((r) => {
    const v = stats[r.key];
    return typeof v === 'number' && v > 0;
  });

  return (
    <div className={EDM_UI.card}>
      <h2 className={EDM_UI.heading}>
        <Send className={EDM_UI.headingIcon} /> Results
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        <StatCard title="Sent" value={stats.sent.toLocaleString('en-AU')} icon={<Send className="h-5 w-5" />} />
        <StatCard title="Delivered" value={delivered.value} subtitle={delivered.subtitle} icon={<CheckCircle className="h-5 w-5" />} />
        <StatCard title="Bounced" value={bounced.value} subtitle={bounced.subtitle} icon={<MailX className="h-5 w-5" />} />
        <StatCard title="Unsubscribed" value={unsubscribed.value} subtitle={unsubscribed.subtitle} icon={<UserMinus className="h-5 w-5" />} />
        <StatCard
          title="Remaining"
          value={(stats.queued + stats.pending).toLocaleString('en-AU')}
          icon={<Hourglass className="h-5 w-5" />}
        />
      </div>
      {rows.length > 0 && (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-gray-800">
                <td className="py-1.5 text-gray-300">{r.label}</td>
                <td className="py-1.5 text-right text-gray-100">{(stats[r.key] as number).toLocaleString('en-AU')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-xs text-gray-400">
        Sent means our email provider accepted the email. Delivered and bounced come from the receiving mail servers. Opens
        and clicks are not tracked.
      </p>
    </div>
  );
};

export default StatsPanel;
